import React, { useCallback, memo, useRef } from 'react';
import { clsx } from 'js/utils/classNames.util';
import { dispatcher } from 'dispatcher';
import { RoundIcon } from '../../../components/RoundIcon';
import { FavoriteRoundIcon } from '../../FavoriteRoundIcon/FavoriteRoundIcon';

import { useShareGameButton } from '../../../components/RoundIcon/hooks/useShareGameButton';
import { ITranslateMethod } from 'js/hooks/i18n/types';
import { useRoundIconsClassRemoval } from '../../hooks/useRoundIconsClassRemoval';
import { useAddAnimateOnOpenClass } from '../../hooks/useAddAnimateOnOpenClass';
import { useAnimationContext } from '../../../context/useAnimationContext';

import style from './RoundIconsWrapper.scss';
import { ANIMATE_ON_OPEN, ROUND_ICONS } from '../../../context/constants';

type RoundIconsWrapperProps = {
    code: string;
    name: string;
    t: ITranslateMethod;
    showFavorite?: boolean;
    processAnimation?: () => void;
    className?: string;
    handleShowGameDetails: () => void;
}

const RoundIconsWrapper = ({ code, name, t, showFavorite = false, processAnimation, className = '', handleShowGameDetails }: RoundIconsWrapperProps) => {
    const shareButton = useShareGameButton({
        code,
        name,
        errorSharingMessage:  t('playtech.mobile.system.game.share.error'),
        notSupportedFeatureMessage: t('playtech.mobile.system.game.share.notSupported')
    });

    const animateRoundIcons = useCallback(() => {
        processAnimation?.();
        dispatcher.dispatch('showGameDetailsAnimation');
        handleShowGameDetails();
    }, [handleShowGameDetails, processAnimation]);

    const { shouldAnimateOnOpen, animationClasses } = useAnimationContext();

    const leftIconRef = useRef<HTMLDivElement | null>(null);
    const middleIconRef = useRef<HTMLDivElement | null>(null);
    const rightIconRef = useRef<HTMLDivElement | null>(null);

    const handleRemoveClass = useRoundIconsClassRemoval(
        style['icon--invisible'],
        [leftIconRef, middleIconRef, rightIconRef]
    );

    useAddAnimateOnOpenClass(handleRemoveClass);

    const [aimateOnOpenClasses] = animationClasses[ANIMATE_ON_OPEN][ROUND_ICONS];

    return (
        <div className={`${style.iconsWrapper} ${className}`}>
            <RoundIcon
                iconRef={leftIconRef}
                containerClassName={clsx([
                    style.leftIcon,
                    style.icon,
                    aimateOnOpenClasses,
                    shouldAnimateOnOpen ? style['icon--invisible'] : ''
                ])}
                type='share'
                onClick={shareButton}
            />
            {showFavorite && (
                <FavoriteRoundIcon
                    iconRef={middleIconRef}
                    containerClassName={clsx([
                        style.middleIcon,
                        style.icon,
                        aimateOnOpenClasses,
                        shouldAnimateOnOpen ? style['icon--invisible'] : ''
                    ])}
                    code={code}
                />
            )}
            <RoundIcon
                iconRef={rightIconRef}
                type='info'
                onClick={animateRoundIcons}
                containerClassName={clsx([
                    style.rightIcon,
                    style.icon,
                    aimateOnOpenClasses,
                    shouldAnimateOnOpen ? style['icon--invisible'] : ''
                ])}
            />
        </div>
    );
};

export default memo(RoundIconsWrapper);
